/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useState } from "react";
import {
  IconX,
  IconUpload,
  IconPrinter,
  IconRefresh,
  IconDownload,
  IconClipboardCheck,
  IconFileSpreadsheet,
} from "@tabler/icons-react";
import { useLanguage } from "../../../context/LanguageContext";
import ButtonStyled from "../../../components/common/ButtonStyled";
import FileUploader from "../../../components/common/FileUploader";
import {
  useOrderBulkDocMutation,
  useFetchOrderTemplate,
  useOrderSyncMutation,
} from "../../../apis/orderApis";
import toast from "react-hot-toast";

interface ActionSidebarProps {
  onClose: () => void;
  selectedOrders: any[];
  setSelectedOrders: (orders: any[]) => void;
  orders: any[];
  refetch: () => void;
  onShowBulkResponse?: (res: any) => void;
}

export default function ActionSidebar({
  onClose,
  selectedOrders = [],
  setSelectedOrders,
  orders = [],
  refetch,
  onShowBulkResponse,
}: ActionSidebarProps) {
  const { t } = useLanguage();
  const [activeAction, setActiveAction] = useState<"upload" | "print" | "sync">("upload");
  const [file, setFile] = useState<File | null>(null);

  const { mutate: uploadDocMutate, isPending: isBulkUploading } = useOrderBulkDocMutation();
  const { data: templateData, refetch: fetchTemplates } = useFetchOrderTemplate();
  const { mutate: syncMutate, isPending: isSyncing } = useOrderSyncMutation();

  const handleUploadDoc = () => {
    if (!file) {
      toast.error(t("Please select a file first"));
      return;
    }
    uploadDocMutate(
      { orders: file },
      {
        onSuccess: (data: any) => {
          if (onShowBulkResponse) {
            onShowBulkResponse(data?.data);
          }
          onClose();
          refetch();
          toast.success(t("Orders uploaded successfully"));
        },
        onError: (error: any) => {
          console.error("Bulk upload error:", error);
          toast.error(error?.message || t("Bulk upload failed"));
        },
      }
    );
  };

  const handleDownloadTemplate = () => {
    if (templateData?.url) {
      window.open(templateData.url, "_blank");
    } else {
      fetchTemplates();
      toast.success(t("Fetching order template..."));
    }
  };

  const handleSyncSelected = () => {
    if (selectedOrders.length === 0) {
      toast.error(t("Please select at least one order to sync"));
      return;
    }
    syncMutate(undefined, {
      onSuccess: () => {
        toast.success(t("Routes synced successfully"));
        refetch();
      },
      onError: (err: any) => {
        toast.error(err?.message || t("Failed to sync routes"));
      },
    });
  };

  const handlePrintSelected = () => {
    if (selectedOrders.length === 0) {
      toast.error(t("Please select at least one order to print"));
      return;
    }
    window.print();
  };

  return (
    <div className="relative border rounded-2xl border-gray-200 flex flex-col bg-white shadow-xl overflow-hidden my-2 w-full max-w-full">
      {/* ── Top Header ── */}
      <div className="flex items-center justify-between px-6 py-4 shrink-0 bg-gradient-to-r from-gray-50 to-white border-b border-gray-200">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-lg bg-yellow-100 flex items-center justify-center text-[#131327]">
            <IconClipboardCheck size={18} />
          </div>
          <div>
            <h2 className="text-base font-bold text-[#212121]">
              {t("Bulk Actions")}
            </h2>
            <p className="text-[11px] text-gray-500">
              {selectedOrders.length > 0
                ? `${selectedOrders.length} ${t("orders selected")}`
                : t("Manage orders in bulk")}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {selectedOrders.length > 0 && (
            <button
              onClick={() => setSelectedOrders([])}
              className="text-xs text-blue-600 hover:text-blue-700 underline mr-1 cursor-pointer"
            >
              {t("Clear")}
            </button>
          )}
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-lg border border-gray-200 hover:bg-gray-100 transition-colors text-slate-700 bg-white cursor-pointer"
            title={t("Close")}
          >
            <IconX size={18} />
          </button>
        </div>
      </div>

      {/* ── Action Tabs ── */}
      <div className="flex items-center gap-1.5 p-3 bg-gray-50 border-b border-gray-200 overflow-x-auto">
        <button
          onClick={() => setActiveAction("upload")}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition-all cursor-pointer ${
            activeAction === "upload"
              ? "bg-[#212121] text-white shadow-sm"
              : "bg-white text-gray-700 hover:bg-gray-100 border border-gray-200"
          }`}
        >
          <IconFileSpreadsheet size={15} />
          <span>{t("Bulk Upload")}</span>
        </button>

        <button
          onClick={() => setActiveAction("print")}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition-all cursor-pointer ${
            activeAction === "print"
              ? "bg-[#212121] text-white shadow-sm"
              : "bg-white text-gray-700 hover:bg-gray-100 border border-gray-200"
          }`}
        >
          <IconPrinter size={15} />
          <span>{t("Print Labels")}</span>
          {selectedOrders.length > 0 && (
            <span className="ml-1 px-1.5 py-0.2 rounded-full text-[10px] bg-blue-100 text-blue-800">
              {selectedOrders.length}
            </span>
          )}
        </button>

        <button
          onClick={() => setActiveAction("sync")}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition-all cursor-pointer ${
            activeAction === "sync"
              ? "bg-[#212121] text-white shadow-sm"
              : "bg-white text-gray-700 hover:bg-gray-100 border border-gray-200"
          }`}
        >
          <IconRefresh size={15} />
          <span>{t("Sync Routes")}</span>
        </button>
      </div>

      {/* ── Action Content ── */}
      <div className="p-6 space-y-6 overflow-y-auto max-h-[calc(100vh-280px)]">
        {/* Upload Action */}
        {activeAction === "upload" && (
          <div className="space-y-4">
            <div className="flex items-center justify-between p-3 rounded-xl bg-yellow-50 border border-yellow-200">
              <div>
                <p className="text-xs font-bold text-gray-800">{t("Download Template")}</p>
                <p className="text-[11px] text-gray-600">
                  {t("Use our standardized Excel template to upload bulk orders.")}
                </p>
              </div>
              <ButtonStyled
                onClick={handleDownloadTemplate}
                variant="outline"
                className="!text-xs !py-1 !px-2.5"
                value={t("Download")}
              />
            </div>

            <div>
              <label className="block text-xs font-bold text-gray-700 mb-2">
                {t("Upload Excel File")} (.xlsx, .xls)
              </label>
              <FileUploader
                name="csvFile"
                extraParentClasses="w-full"
                img="/img/upload-img.png"
                title={t("Upload CSV / Excel File")}
                message={t("Upload a .xlsx or .xls file (Max size: 5 MB).")}
                classNameMain="bg-transparent border-none flex items-center gap-3"
                fileVal={file}
                setFileVal={(f: any) => setFile(f)}
                showPreview={true}
                uploadType="document"
                accept=".xlsx, .xls"
              />
            </div>

            <div className="pt-2">
              <ButtonStyled
                onClick={handleUploadDoc}
                disabled={!file || isBulkUploading}
                isPending={isBulkUploading}
                variant="solid"
                className="w-full !py-2.5"
                value={t("Upload Orders")}
              />
            </div>
          </div>
        )}

        {/* Print Action */}
        {activeAction === "print" && (
          <div className="space-y-4">
            <div className="p-4 rounded-xl bg-blue-50 border border-blue-200 text-xs text-blue-900">
              <p className="font-bold mb-1">{t("Bulk Label Printing")}</p>
              <p>
                {selectedOrders.length > 0
                  ? t(`Ready to print shipping labels for ${selectedOrders.length} selected orders.`)
                  : t("Please select orders from the table using checkboxes to print their shipping labels.")}
              </p>
            </div>

            {selectedOrders.length > 0 && (
              <div className="space-y-2">
                <p className="text-xs font-bold text-gray-700">
                  {t("Selected Orders")}:
                </p>
                <div className="max-h-48 overflow-y-auto border border-gray-200 rounded-lg p-2 space-y-1">
                  {selectedOrders.map((o) => (
                    <div
                      key={o.id || o.awbNumber}
                      className="flex items-center justify-between px-2 py-1 text-xs bg-gray-50 rounded"
                    >
                      <span className="font-mono font-semibold">{o.awbNumber}</span>
                      <span className="text-gray-500">{o.receiverName || o.customerName}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <ButtonStyled
              onClick={handlePrintSelected}
              disabled={selectedOrders.length === 0}
              variant="solid"
              className="w-full !py-2.5"
              value={t("Print Selected Labels")}
            />
          </div>
        )}

        {/* Sync Action */}
        {activeAction === "sync" && (
          <div className="space-y-4">
            <div className="p-4 rounded-xl bg-gray-50 border border-gray-200 text-xs text-gray-700">
              <p className="font-bold mb-1">{t("Sync Routes")}</p>
              <p>
                {t(
                  "Sync all assigned routes and shipping details with line haul and delivery providers."
                )}
              </p>
            </div>

            <ButtonStyled
              onClick={handleSyncSelected}
              disabled={isSyncing}
              isPending={isSyncing}
              variant="solid"
              className="w-full !py-2.5"
              value={t("Sync Routes Now")}
            />
          </div>
        )}
      </div>
    </div>
  );
}
